import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { FileText, Loader2, ZoomIn, ZoomOut, RotateCcw, Download, X } from "lucide-react";

export function ReceiptThumb({ shiftName, idx, name, size = 56 }) {
  const [blob, setBlob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const isPdf = (name || "").toLowerCase().endsWith(".pdf");

  useEffect(() => {
    let objUrl;
    api.fetchReceiptBlob(shiftName, idx)
      .then((r) => { objUrl = r.url; setBlob(r); })
      .catch(() => setBlob(null))
      .finally(() => setLoading(false));
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [shiftName, idx]);

  if (loading) return <Loader2 className="h-4 w-4 animate-spin text-slate-400" data-testid="receipt-loading" />;
  if (!blob) return <span className="text-[11px] text-red-500">receipt unavailable</span>;

  const pdf = isPdf || blob.type === "application/pdf";

  return (
    <>
      {pdf ? (
        <button onClick={() => setOpen(true)} data-testid="receipt-open"
          className="inline-flex items-center gap-1 text-[11px] text-blue-600 underline">
          <FileText className="h-3.5 w-3.5" /> {name || "Receipt"} (PDF)
        </button>
      ) : (
        <img src={blob.url} onClick={() => setOpen(true)} alt="receipt" data-testid="receipt-thumb"
          style={{ height: size, width: size }}
          className="rounded-md object-cover border border-slate-200 cursor-zoom-in hover:opacity-80 transition-opacity" />
      )}
      {open && (
        <ReceiptLightbox url={blob.url} name={name} isPdf={pdf} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function ReceiptLightbox({ url, name, isPdf, onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const reset = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const zoom = (delta) => setScale((s) => Math.min(6, Math.max(1, +(s + delta).toFixed(2))));
  const onWheel = (e) => { e.preventDefault(); zoom(e.deltaY < 0 ? 0.3 : -0.3); };
  const onDown = (e) => { if (scale > 1) drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; };
  const onMove = (e) => { if (drag.current) setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); };
  const onUp = () => { drag.current = null; };

  return (
    <div className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex flex-col"
      data-testid="receipt-lightbox" onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}>
      {/* toolbar */}
      <div className="flex items-center justify-between px-4 py-3 text-white/90" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-medium truncate max-w-[60%]">{name || "Receipt"}</div>
        <div className="flex items-center gap-1">
          {!isPdf && (<>
            <button onClick={() => zoom(-0.3)} data-testid="lightbox-zoom-out" className="h-9 w-9 rounded-full hover:bg-white/15 flex items-center justify-center"><ZoomOut className="h-4 w-4" /></button>
            <span className="text-xs w-10 text-center tabular-nums">{Math.round(scale * 100)}%</span>
            <button onClick={() => zoom(0.3)} data-testid="lightbox-zoom-in" className="h-9 w-9 rounded-full hover:bg-white/15 flex items-center justify-center"><ZoomIn className="h-4 w-4" /></button>
            <button onClick={reset} data-testid="lightbox-reset" className="h-9 w-9 rounded-full hover:bg-white/15 flex items-center justify-center"><RotateCcw className="h-4 w-4" /></button>
          </>)}
          <a href={url} download={name || "receipt"} data-testid="lightbox-download" className="h-9 w-9 rounded-full hover:bg-white/15 flex items-center justify-center"><Download className="h-4 w-4" /></a>
          <button onClick={onClose} data-testid="lightbox-close" className="h-9 w-9 rounded-full hover:bg-white/15 flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
      </div>
      {/* body */}
      <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center p-4" onClick={onClose}>
        {isPdf ? (
          <iframe title="receipt" src={url} onClick={(e) => e.stopPropagation()}
            className="w-full h-full max-w-4xl bg-white rounded-md" />
        ) : (
          <img src={url} alt="receipt" draggable={false}
            onClick={(e) => e.stopPropagation()} onWheel={onWheel} onMouseDown={onDown}
            style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, cursor: scale > 1 ? "grab" : "zoom-in" }}
            onDoubleClick={() => (scale > 1 ? reset() : setScale(2.5))}
            className="max-h-full max-w-full object-contain select-none transition-transform duration-75" />
        )}
      </div>
      <div className="text-center text-white/40 text-[11px] pb-3">Scroll to zoom · drag to pan · double-click to toggle · Esc to close</div>
    </div>
  );
}
